import { createFramebuffer, createRenderTexture, createStateBuffer } from "../gl/buffers";
import { Simulation, type SimUniforms } from "../sim/simulation";
import { EngineError, type ExportScale } from "../types";
import { Renderer, type RenderPrograms, type Rgb, type StepUniforms } from "./renderer";

/** Conservative dimension cap — GPUs advertise more than they render well. */
export const EXPORT_DIM_CAP = 8192;
/** 16F trail FBO memory budget (bytes) — the likely OOM/context-loss trigger. */
export const EXPORT_MEMORY_BUDGET = 300 * 1024 * 1024;
const TRAIL_BYTES_PER_PIXEL = 8; // RGBA16F
/** Replay steps per macrotask so the tab stays responsive. */
export const REPLAY_CHUNK = 40;

export function computeMaxExportScale(
  cssWidth: number,
  cssHeight: number,
  maxTextureSize: number,
  budgetBytes: number = EXPORT_MEMORY_BUDGET,
): ExportScale {
  const cap = Math.min(maxTextureSize, EXPORT_DIM_CAP);
  for (const scale of [4, 3, 2] as const) {
    const w = Math.round(cssWidth * scale);
    const h = Math.round(cssHeight * scale);
    if (w <= cap && h <= cap && w * h * TRAIL_BYTES_PER_PIXEL <= budgetBytes) {
      return scale;
    }
  }
  return 1;
}

/** Everything the exporter needs from the live engine, no back-reference. */
export interface ExportHost {
  gl: WebGL2RenderingContext;
  sim: Simulation;
  programs: RenderPrograms;
  preferFloatTrails: boolean;
  maxPointSize: number;
  cssWidth: number;
  cssHeight: number;
  maxTextureSize: number;
  /** Steps at which the current plot settles (see plotTotalSteps). */
  totalSteps: number;
  /** True once the engine that spawned this export has been destroyed. */
  aborted(): boolean;
  /** Fresh step-0 strand state for the current seed + params. */
  buildState(): Float32Array;
  bg(): Rgb;
  exposure(): number;
  simUniforms(): SimUniforms;
  stepUniforms(resScale: number): StepUniforms;
}

const macrotask = () => new Promise<void>((r) => setTimeout(r, 0));

function flipRows(pixels: Uint8Array, width: number, height: number): void {
  const rowBytes = width * 4;
  const tmp = new Uint8Array(rowBytes);
  for (let y = 0; y < height >> 1; y++) {
    const top = y * rowBytes;
    const bottom = (height - 1 - y) * rowBytes;
    tmp.set(pixels.subarray(top, top + rowBytes));
    pixels.copyWithin(top, bottom, bottom + rowBytes);
    pixels.set(tmp, bottom);
  }
}

async function encodePng(
  pixels: Uint8Array<ArrayBuffer>,
  width: number,
  height: number,
): Promise<Blob> {
  const data = new ImageData(
    new Uint8ClampedArray(pixels.buffer, 0, width * height * 4),
    width,
    height,
  );
  if (typeof OffscreenCanvas !== "undefined") {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.putImageData(data, 0, 0);
      return canvas.convertToBlob({ type: "image/png" });
    }
  }
  // Older Safari: hidden 2D canvas, never attached to the document.
  const el = document.createElement("canvas");
  el.width = width;
  el.height = height;
  const ctx = el.getContext("2d");
  if (!ctx) throw new Error("2d canvas unavailable for PNG encoding");
  ctx.putImageData(data, 0, 0);
  return new Promise<Blob>((resolve, reject) => {
    el.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob failed"))),
      "image/png",
    );
  });
}

/**
 * Deterministic hi-res export: full replay from step 0. The plot is a pure
 * function of seed + params, so re-running exactly the live engine's step
 * count into an N-scale offscreen trail FBO reproduces what the user sees —
 * including a mid-draw partial plot. Present, read back, encode.
 * The caller must have paused the live loop.
 */
export async function exportPng(
  host: ExportHost,
  scale: ExportScale,
  onProgress?: (t: number) => void,
): Promise<Blob> {
  const gl = host.gl;
  const width = Math.round(host.cssWidth * scale);
  const height = Math.round(host.cssHeight * scale);
  const cap = Math.min(host.maxTextureSize, EXPORT_DIM_CAP);
  if (
    width < 1 ||
    height < 1 ||
    width > cap ||
    height > cap ||
    width * height * TRAIL_BYTES_PER_PIXEL > EXPORT_MEMORY_BUDGET
  ) {
    throw new EngineError(
      "export-too-large",
      `export of ${width}x${height} exceeds device limits (max dim ${cap})`,
    );
  }

  // Re-checked at every chunk boundary: a lost context turns every GL call
  // into a silent no-op, which would otherwise resolve with a black PNG.
  const ensureLive = (): void => {
    if (host.aborted()) {
      throw new EngineError("context-lost", "engine destroyed during export");
    }
    if (gl.isContextLost()) throw new EngineError("context-lost");
  };

  onProgress?.(0);
  const sim = host.sim;
  const savedStep = sim.stepCount;
  const backup = createStateBuffer(gl, sim.byteLength);
  let renderer: Renderer | null = null;
  let presentTex: WebGLTexture | null = null;
  let presentFbo: WebGLFramebuffer | null = null;

  try {
    sim.captureTo(backup);

    renderer = new Renderer(gl, host.programs, host.preferFloatTrails, host.maxPointSize);
    renderer.setSize(width, height, host.bg());
    presentTex = createRenderTexture(gl, width, height, false);
    presentFbo = createFramebuffer(gl, presentTex);
    if (!presentFbo) {
      throw new EngineError("export-too-large", "export framebuffer incomplete");
    }

    // Replay exactly as many steps as the live plot has drawn (a settled
    // plot never exceeds totalSteps; min() is a guard, not a truncation).
    sim.reset(host.buildState(), 0);
    const total = Math.min(savedStep, host.totalSteps);
    const su = host.simUniforms();
    const du = host.stepUniforms(height / host.cssHeight);

    let done = 0;
    while (done < total) {
      ensureLive();
      const n = Math.min(REPLAY_CHUNK, total - done);
      for (let i = 0; i < n; i++) {
        sim.step(su);
        renderer.drawStep(sim.currentVao, sim.count, du);
      }
      done += n;
      onProgress?.(0.9 * (done / total));
      await macrotask();
    }

    ensureLive();
    renderer.present(presentFbo, host.exposure(), sim.stepCount);
    const pixels = new Uint8Array(width * height * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, presentFbo);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    flipRows(pixels, width, height);
    for (let i = 3; i < pixels.length; i += 4) pixels[i] = 255;
    ensureLive();
    onProgress?.(0.95);
    const blob = await encodePng(pixels, width, height);
    onProgress?.(1);
    return blob;
  } finally {
    // Replay is bit-exact so this is normally a no-op copy, but it also
    // recovers the live state after a mid-replay failure. A destroyed engine
    // or lost context has nothing left to restore into.
    if (!host.aborted() && !gl.isContextLost()) {
      sim.restore(backup, savedStep);
    }
    gl.deleteBuffer(backup);
    if (presentFbo) gl.deleteFramebuffer(presentFbo);
    if (presentTex) gl.deleteTexture(presentTex);
    renderer?.destroy();
  }
}
